<template>
    <div
        class="sidebar-container bg-white rounded-lg shadow-sm relative"
        :style="{ '--active-color': activeColor }"
    >
        <!-- Loading State -->
        <div v-if="loading" class="faq-loading-container">
            <div class="faq-loader text-primary-darker">
                <i class="feather icon-loader animate-spin"></i>
                <p class="faq-loader-text text-onyx">Loading categories...</p>
            </div>
        </div>

        <!-- Categories list -->
        <nav v-else-if="categories.length > 0" class="lg:pl-4">
            <ul class="space-y-3 md:space-y-0">
                <li
                    v-for="(category, index) in categories"
                    :key="category.id"
                    class="category-item rounded-lg md:rounded-none"
                    :class="{
                        'border-b border-gray-200':
                            index !== categories.length - 1,
                    }"
                >
                    <a
                        href="#"
                        @click.prevent="setActiveCategory(category.id)"
                        :class="[
                            'block py-3 md:py-4 px-4 text-14 lg:text-base font-semibold transition-colors rounded-lg category-link',
                            activeCategory === category.id
                                ? 'category-link-active'
                                : 'category-link-inactive',
                        ]"
                    >
                        {{ category.title }}
                    </a>
                </li>
            </ul>
        </nav>

        <!-- No Results State -->
        <div
            v-else
            class="flex justify-center items-center min-h-400 py-10 px-5"
        >
            <div class="flex flex-col items-center justify-center gap-4">
                <i
                    class="feather icon-search text-24 text-onyx-lightest opacity-50"
                ></i>
                <p class="text-14 font-medium text-onyx-lightest">
                    No matching categories found
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'SideBar',
    props: {
        searchKeyword: {
            type: String,
            default: '',
        },
        activeColor: {
            type: String,
            default: '#b32026',
        },
    },
    data() {
        return {
            activeCategory: null,
            categories: [],
            loading: false,
            searchDebounceTimer: null,
        };
    },
    mounted() {
        this.getCategories();
    },
    watch: {
        searchKeyword(newValue) {
            // Clear existing timer
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }

            // Set new timer
            this.searchDebounceTimer = setTimeout(() => {
                this.getCategories();
            }, 500); // 500ms debounce
        },
    },
    methods: {
        async getCategories() {
            this.loading = true;
            let url = '/api/faq/categories';

            if (this.searchKeyword) {
                url += `?search=${encodeURIComponent(this.searchKeyword)}`;
            }

            const { data } = await axios.get(url);

            this.categories = data.categories;
            if (this.categories.length > 0) {
                this.activeCategory = this.categories[0].id;
                this.$emit('category-changed', this.activeCategory);
            } else {
                // When searching and no categories found, emit null to search all questions
                this.activeCategory = null;
                this.$emit('category-changed', null);
            }
            this.loading = false;
        },
        setActiveCategory(categoryId) {
            this.activeCategory = categoryId;
            this.$emit('category-changed', categoryId);
        },
    },
};
</script>

<style scoped>
/* Mobile styles */
@media (max-width: 767px) {
    .category-item {
        background-color: #f5f5f5;
    }

    .category-link-inactive {
        background-color: #f5f5f5;
        color: #202028;
    }

    .category-link-active {
        background-color: var(--active-color) !important;
        color: white !important;
    }
}

/* Tablet/Desktop styles */
@media (min-width: 768px) {
    .category-item {
        border-bottom: 1px solid rgba(217, 217, 217, 0.5) !important;
    }

    .category-item:last-child {
        border-bottom: none !important;
    }

    .category-link-inactive {
        background-color: white;
        color: #202028;
    }

    .category-link-inactive:hover {
        color: var(--active-color) !important;
    }

    .category-link-active {
        background-color: white !important;
        color: var(--active-color) !important;
    }
}

/* Custom scrollbar for sidebar when it overflows */
.sidebar-container {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 #f7fafc;
}

.sidebar-container::-webkit-scrollbar {
    width: 6px;
}

.sidebar-container::-webkit-scrollbar-track {
    background: #f7fafc;
}

.sidebar-container::-webkit-scrollbar-thumb {
    background-color: #cbd5e0;
    border-radius: 3px;
}

/* Loading State */
.faq-loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    padding: 40px 20px;
}

.faq-loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
}

.faq-loader i {
    font-size: 24px;
}

.faq-loader-text {
    font-size: 14px;
    font-weight: 500;
}
</style>
